import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Check, Package, MapPin, Phone, Wrench, Clock, Shield, Search, Zap, Loader2, LogIn, User, Eye, EyeOff, PartyPopper } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { SuccessAnimation } from '../components/ui/animations';
import { CardSkeleton } from '../components/ui/skeleton-loader';
import SmsOptInCheckbox, { SMS_OPT_IN_CONSENT_TEXT } from '../shared/SmsOptInCheckbox';
import SEO from '../components/SEO';
import ProtectionPlanNotice from '../components/shared/ProtectionPlanNotice';
// Address autocomplete removed - using simple input

const API_URL = (() => { const envUrl = process.env.REACT_APP_BACKEND_URL; if (!envUrl || envUrl === '') return window.location.origin; try { const envHost = new URL(envUrl).hostname; const currentHost = window.location.hostname; if (currentHost === 'localhost' || currentHost === '127.0.0.1' || envHost === currentHost || currentHost.includes('preview.emergentagent.com')) { return envUrl; } return window.location.origin; } catch (e) { return window.location.origin; } })();

// Per-category SEO targeting Chicago search keywords. Categories WITHOUT a
// dedicated -chicago landing page get the geo keyword here (tablets, playstation,
// xbox); the rest get clean booking-oriented titles that don't cannibalize their
// dedicated SEO pages (e.g. /laptop-repair-chicago).
const REPAIR_CATEGORY_SEO = {
  tablets: {
    title: 'Tablet Repair Chicago | iPad, Galaxy Tab, Surface | 2A Electronics',
    description: 'Tablet repair in Chicago, same-day in most cases. Cracked screens, batteries and charging ports fixed on iPad, Samsung Galaxy Tab and Microsoft Surface. Free diagnostics, 1-month warranty.',
    keywords: 'tablet repair chicago, ipad repair chicago, galaxy tab repair chicago, surface repair chicago, tablet screen repair chicago',
    h1: ['Tablet Repair', 'in Chicago'],
  },
  playstation: {
    title: 'PlayStation Repair Chicago | PS5 & PS4 HDMI, Overheating | 2A Electronics',
    description: 'PlayStation repair in Chicago. PS5 and PS4 HDMI port replacement, overheating and fan service, disc drive repair and more. Free diagnostics, fast turnaround, 1-month warranty.',
    keywords: 'playstation repair chicago, ps5 repair chicago, ps4 repair chicago, ps5 hdmi port repair chicago, playstation hdmi repair chicago',
    h1: ['PlayStation Repair', 'in Chicago'],
  },
  xbox: {
    title: 'Xbox Repair Chicago | Series X, Series S & One | 2A Electronics',
    description: 'Xbox repair in Chicago. Series X, Series S and Xbox One HDMI port replacement, overheating and fan service, disc drive repair. Free diagnostics, 1-month warranty.',
    keywords: 'xbox repair chicago, xbox series x repair chicago, xbox hdmi port repair chicago, xbox one repair chicago',
    h1: ['Xbox Repair', 'in Chicago'],
  },
};

// Default services used when category doesn't have specific services from API
const defaultServices = [
  { id: 'screen', name: 'Screen Replacement', price: 'Call for Quote', time: '1-2 hours' },
  { id: 'battery', name: 'Battery Replacement', price: 'Call for Quote', time: '1 hour' },
  { id: 'charging-port', name: 'Charging Port Repair', price: 'Call for Quote', time: '1 hour' },
  { id: 'other', name: 'Other Issue (Describe below)', price: 'Call for Quote', time: 'Varies' },
];

// Static repair catalog - THIS IS THE SOURCE OF TRUTH for all repair devices
// This ensures devices are always available even if the database is reset
const staticRepairCatalog = {
  // TABLETS
  tablets: {
    title: 'Tablet Repair',
    description: 'iPad, Samsung Galaxy Tab, Microsoft Surface repair',
    devices: [
      // iPads (2020-2025)
      // 2025 Models
      { id: 'ipad-11th-gen', name: 'iPad (11th Gen)', brand: 'Apple', year: 2025 },
      { id: 'ipad-air-13-m3', name: 'iPad Air 13" M3', brand: 'Apple', year: 2025 },
      { id: 'ipad-air-11-m3', name: 'iPad Air 11" M3', brand: 'Apple', year: 2025 },
      // 2024 Models
      { id: 'ipad-pro-13-m4', name: 'iPad Pro 13" M4', brand: 'Apple', year: 2024 },
      { id: 'ipad-pro-11-m4', name: 'iPad Pro 11" M4', brand: 'Apple', year: 2024 },
      { id: 'ipad-air-13-m2', name: 'iPad Air 13" M2', brand: 'Apple', year: 2024 },
      { id: 'ipad-air-11-m2', name: 'iPad Air 11" M2', brand: 'Apple', year: 2024 },
      { id: 'ipad-mini-7', name: 'iPad Mini (7th Gen)', brand: 'Apple', year: 2024 },
      // 2022-2023 Models
      { id: 'ipad-10th-gen', name: 'iPad (10th Gen)', brand: 'Apple', year: 2022 },
      { id: 'ipad-mini-6', name: 'iPad Mini (6th Gen)', brand: 'Apple', year: 2021 },
      { id: 'ipad-pro-12-9-6th', name: 'iPad Pro 12.9" (6th Gen)', brand: 'Apple', year: 2022 },
      { id: 'ipad-pro-11-4th', name: 'iPad Pro 11" (4th Gen)', brand: 'Apple', year: 2022 },
      { id: 'ipad-air-5th', name: 'iPad Air (5th Gen)', brand: 'Apple', year: 2022 },
      { id: 'ipad-9th-gen', name: 'iPad (9th Gen)', brand: 'Apple', year: 2021 },
      { id: 'ipad-pro-12-9-5th', name: 'iPad Pro 12.9" (5th Gen)', brand: 'Apple', year: 2021 },
      { id: 'ipad-pro-11-3rd', name: 'iPad Pro 11" (3rd Gen)', brand: 'Apple', year: 2021 },
      { id: 'ipad-air-4th', name: 'iPad Air (4th Gen)', brand: 'Apple', year: 2020 },
      { id: 'ipad-8th-gen', name: 'iPad (8th Gen)', brand: 'Apple', year: 2020 },
      // Samsung Tablets (2020-2025)
      { id: 'galaxy-tab-s10-ultra', name: 'Galaxy Tab S10 Ultra', brand: 'Samsung', year: 2024 },
      { id: 'galaxy-tab-s10-plus', name: 'Galaxy Tab S10+', brand: 'Samsung', year: 2024 },
      { id: 'galaxy-tab-s10', name: 'Galaxy Tab S10', brand: 'Samsung', year: 2024 },
      { id: 'galaxy-tab-s9-ultra', name: 'Galaxy Tab S9 Ultra', brand: 'Samsung', year: 2023 },
      { id: 'galaxy-tab-s9-plus', name: 'Galaxy Tab S9+', brand: 'Samsung', year: 2023 },
      { id: 'galaxy-tab-s9', name: 'Galaxy Tab S9', brand: 'Samsung', year: 2023 },
      { id: 'galaxy-tab-s9-fe-plus', name: 'Galaxy Tab S9 FE+', brand: 'Samsung', year: 2023 },
      { id: 'galaxy-tab-s9-fe', name: 'Galaxy Tab S9 FE', brand: 'Samsung', year: 2023 },
      { id: 'galaxy-tab-s8-ultra', name: 'Galaxy Tab S8 Ultra', brand: 'Samsung', year: 2022 },
      { id: 'galaxy-tab-s8-plus', name: 'Galaxy Tab S8+', brand: 'Samsung', year: 2022 },
      { id: 'galaxy-tab-s8', name: 'Galaxy Tab S8', brand: 'Samsung', year: 2022 },
      { id: 'galaxy-tab-s7-plus', name: 'Galaxy Tab S7+', brand: 'Samsung', year: 2020 },
      { id: 'galaxy-tab-s7', name: 'Galaxy Tab S7', brand: 'Samsung', year: 2020 },
      { id: 'galaxy-tab-a9-plus', name: 'Galaxy Tab A9+', brand: 'Samsung', year: 2023 },
      { id: 'galaxy-tab-a9', name: 'Galaxy Tab A9', brand: 'Samsung', year: 2023 },
      { id: 'galaxy-tab-a8', name: 'Galaxy Tab A8', brand: 'Samsung', year: 2022 },
      // Microsoft Surface (2020-2025)
      { id: 'surface-pro-11', name: 'Surface Pro 11', brand: 'Microsoft', year: 2024 },
      { id: 'surface-pro-10', name: 'Surface Pro 10 Business', brand: 'Microsoft', year: 2024 },
      { id: 'surface-pro-9', name: 'Surface Pro 9', brand: 'Microsoft', year: 2022 },
      { id: 'surface-pro-8', name: 'Surface Pro 8', brand: 'Microsoft', year: 2021 },
      { id: 'surface-pro-7-plus', name: 'Surface Pro 7+', brand: 'Microsoft', year: 2021 },
      { id: 'surface-pro-7', name: 'Surface Pro 7', brand: 'Microsoft', year: 2020 },
      { id: 'surface-go-4', name: 'Surface Go 4', brand: 'Microsoft', year: 2023 },
      { id: 'surface-go-3', name: 'Surface Go 3', brand: 'Microsoft', year: 2021 },
      // Google Pixel Tablet
      { id: 'pixel-tablet', name: 'Pixel Tablet', brand: 'Google', year: 2023 },
      // Amazon Fire Tablets
      { id: 'fire-max-11', name: 'Fire Max 11', brand: 'Amazon', year: 2023 },
      { id: 'fire-hd-10-2023', name: 'Fire HD 10 (2023)', brand: 'Amazon', year: 2023 },
      { id: 'fire-hd-8-2024', name: 'Fire HD 8 (2024)', brand: 'Amazon', year: 2024 },
      { id: 'other-tablet', name: 'Other Tablet', brand: 'Other', year: null },
    ],
    services: [
      { id: 'screen', name: 'Screen Replacement', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'battery', name: 'Battery Replacement', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'charging-port', name: 'Charging Port Repair', price: 'Call for Quote', time: '1 hour' },
      { id: 'camera', name: 'Camera Repair', price: 'Call for Quote', time: '1 hour' },
      { id: 'button', name: 'Button Repair', price: 'Call for Quote', time: '1 hour' },
      { id: 'other', name: 'Other Issue', price: 'Call for Quote', time: 'Varies' },
    ]
  },
  // LAPTOPS
  laptops: {
    title: 'Laptop Repair',
    description: 'MacBook, Dell, HP, Lenovo, ASUS laptop repair',
    devices: [
      // MacBooks (2020-2025)
      // 2025 Models
      { id: 'macbook-air-15-m4', name: 'MacBook Air 15" M4', brand: 'Apple', year: 2025 },
      { id: 'macbook-air-13-m4', name: 'MacBook Air 13" M4', brand: 'Apple', year: 2025 },
      // 2024 Models
      { id: 'macbook-pro-16-m4-max', name: 'MacBook Pro 16" M4 Max', brand: 'Apple', year: 2024 },
      { id: 'macbook-pro-16-m4-pro', name: 'MacBook Pro 16" M4 Pro', brand: 'Apple', year: 2024 },
      { id: 'macbook-pro-14-m4-max', name: 'MacBook Pro 14" M4 Max', brand: 'Apple', year: 2024 },
      { id: 'macbook-pro-14-m4-pro', name: 'MacBook Pro 14" M4 Pro', brand: 'Apple', year: 2024 },
      { id: 'macbook-pro-14-m4', name: 'MacBook Pro 14" M4', brand: 'Apple', year: 2024 },
      { id: 'macbook-air-15-m3', name: 'MacBook Air 15" M3', brand: 'Apple', year: 2024 },
      { id: 'macbook-air-13-m3', name: 'MacBook Air 13" M3', brand: 'Apple', year: 2024 },
      // 2023 Models
      { id: 'macbook-pro-16-m3-max', name: 'MacBook Pro 16" M3 Max', brand: 'Apple', year: 2023 },
      { id: 'macbook-pro-16-m3-pro', name: 'MacBook Pro 16" M3 Pro', brand: 'Apple', year: 2023 },
      { id: 'macbook-pro-14-m3-max', name: 'MacBook Pro 14" M3 Max', brand: 'Apple', year: 2023 },
      { id: 'macbook-pro-14-m3-pro', name: 'MacBook Pro 14" M3 Pro', brand: 'Apple', year: 2023 },
      { id: 'macbook-pro-14-m3', name: 'MacBook Pro 14" M3', brand: 'Apple', year: 2023 },
      // 2022 Models
      { id: 'macbook-air-15-m2', name: 'MacBook Air 15" M2', brand: 'Apple', year: 2022 },
      { id: 'macbook-air-13-m2', name: 'MacBook Air 13" M2', brand: 'Apple', year: 2022 },
      { id: 'macbook-pro-16-m2-max', name: 'MacBook Pro 16" M2 Max', brand: 'Apple', year: 2022 },
      { id: 'macbook-pro-16-m2-pro', name: 'MacBook Pro 16" M2 Pro', brand: 'Apple', year: 2022 },
      { id: 'macbook-pro-14-m2-max', name: 'MacBook Pro 14" M2 Max', brand: 'Apple', year: 2022 },
      { id: 'macbook-pro-14-m2-pro', name: 'MacBook Pro 14" M2 Pro', brand: 'Apple', year: 2022 },
      { id: 'macbook-pro-13-m2', name: 'MacBook Pro 13" M2', brand: 'Apple', year: 2022 },
      // 2021 Models
      { id: 'macbook-pro-16-m1-max', name: 'MacBook Pro 16" M1 Max', brand: 'Apple', year: 2021 },
      { id: 'macbook-pro-16-m1-pro', name: 'MacBook Pro 16" M1 Pro', brand: 'Apple', year: 2021 },
      { id: 'macbook-pro-14-m1-max', name: 'MacBook Pro 14" M1 Max', brand: 'Apple', year: 2021 },
      { id: 'macbook-pro-14-m1-pro', name: 'MacBook Pro 14" M1 Pro', brand: 'Apple', year: 2021 },
      // 2020 Models
      { id: 'macbook-air-m1', name: 'MacBook Air M1', brand: 'Apple', year: 2020 },
      { id: 'macbook-pro-13-m1', name: 'MacBook Pro 13" M1', brand: 'Apple', year: 2020 },
      // Dell (2020-2025)
      { id: 'dell-xps-16-2025', name: 'Dell XPS 16 (2025)', brand: 'Dell', year: 2025 },
      { id: 'dell-xps-14-2025', name: 'Dell XPS 14 (2025)', brand: 'Dell', year: 2025 },
      { id: 'dell-xps-13-2025', name: 'Dell XPS 13 (2025)', brand: 'Dell', year: 2025 },
      { id: 'dell-xps-16-2024', name: 'Dell XPS 16 (2024)', brand: 'Dell', year: 2024 },
      { id: 'dell-xps-14-2024', name: 'Dell XPS 14 (2024)', brand: 'Dell', year: 2024 },
      { id: 'dell-xps-13-2024', name: 'Dell XPS 13 (2024)', brand: 'Dell', year: 2024 },
      { id: 'dell-xps-15-2023', name: 'Dell XPS 15 (2023)', brand: 'Dell', year: 2023 },
      { id: 'dell-xps-13-plus', name: 'Dell XPS 13 Plus', brand: 'Dell', year: 2022 },
      { id: 'dell-inspiron-16-2024', name: 'Dell Inspiron 16 (2024)', brand: 'Dell', year: 2024 },
      { id: 'dell-inspiron-14-2024', name: 'Dell Inspiron 14 (2024)', brand: 'Dell', year: 2024 },
      { id: 'dell-latitude-7450', name: 'Dell Latitude 7450', brand: 'Dell', year: 2024 },
      { id: 'dell-latitude-5450', name: 'Dell Latitude 5450', brand: 'Dell', year: 2024 },
      // HP (2020-2025)
      { id: 'hp-spectre-x360-16-2025', name: 'HP Spectre x360 16 (2025)', brand: 'HP', year: 2025 },
      { id: 'hp-spectre-x360-14-2025', name: 'HP Spectre x360 14 (2025)', brand: 'HP', year: 2025 },
      { id: 'hp-spectre-x360-16-2024', name: 'HP Spectre x360 16 (2024)', brand: 'HP', year: 2024 },
      { id: 'hp-spectre-x360-14-2024', name: 'HP Spectre x360 14 (2024)', brand: 'HP', year: 2024 },
      { id: 'hp-envy-x360-15-2024', name: 'HP ENVY x360 15 (2024)', brand: 'HP', year: 2024 },
      { id: 'hp-envy-x360-14-2024', name: 'HP ENVY x360 14 (2024)', brand: 'HP', year: 2024 },
      { id: 'hp-omen-16-2024', name: 'HP OMEN 16 (2024)', brand: 'HP', year: 2024 },
      { id: 'hp-omen-transcend-16', name: 'HP OMEN Transcend 16', brand: 'HP', year: 2024 },
      { id: 'hp-pavilion-plus-16', name: 'HP Pavilion Plus 16', brand: 'HP', year: 2023 },
      { id: 'hp-elitebook-1040-g10', name: 'HP EliteBook 1040 G10', brand: 'HP', year: 2023 },
      // Lenovo (2020-2025)
      { id: 'lenovo-thinkpad-x1-carbon-gen13', name: 'ThinkPad X1 Carbon Gen 13', brand: 'Lenovo', year: 2025 },
      { id: 'lenovo-yoga-pro-9i-2025', name: 'Yoga Pro 9i (2025)', brand: 'Lenovo', year: 2025 },
      { id: 'lenovo-thinkpad-x1-carbon-gen12', name: 'ThinkPad X1 Carbon Gen 12', brand: 'Lenovo', year: 2024 },
      { id: 'lenovo-yoga-9i-2024', name: 'Yoga 9i (2024)', brand: 'Lenovo', year: 2024 },
      { id: 'lenovo-legion-pro-7i-2024', name: 'Legion Pro 7i (2024)', brand: 'Lenovo', year: 2024 },
      { id: 'lenovo-legion-pro-5i-2024', name: 'Legion Pro 5i (2024)', brand: 'Lenovo', year: 2024 },
      { id: 'lenovo-ideapad-pro-5i', name: 'IdeaPad Pro 5i', brand: 'Lenovo', year: 2024 },
      { id: 'lenovo-thinkpad-t14s-gen5', name: 'ThinkPad T14s Gen 5', brand: 'Lenovo', year: 2024 },
      // ASUS (2020-2025)
      { id: 'asus-zenbook-s16-2025', name: 'ASUS Zenbook S 16 (2025)', brand: 'ASUS', year: 2025 },
      { id: 'asus-zenbook-14-oled-2025', name: 'ASUS Zenbook 14 OLED (2025)', brand: 'ASUS', year: 2025 },
      { id: 'asus-rog-zephyrus-g16-2025', name: 'ASUS ROG Zephyrus G16 (2025)', brand: 'ASUS', year: 2025 },
      { id: 'asus-zenbook-s16-2024', name: 'ASUS Zenbook S 16 (2024)', brand: 'ASUS', year: 2024 },
      { id: 'asus-zenbook-14-oled-2024', name: 'ASUS Zenbook 14 OLED (2024)', brand: 'ASUS', year: 2024 },
      { id: 'asus-rog-zephyrus-g16-2024', name: 'ASUS ROG Zephyrus G16 (2024)', brand: 'ASUS', year: 2024 },
      { id: 'asus-rog-strix-g18-2024', name: 'ASUS ROG Strix G18 (2024)', brand: 'ASUS', year: 2024 },
      { id: 'asus-vivobook-pro-16-oled', name: 'ASUS Vivobook Pro 16 OLED', brand: 'ASUS', year: 2023 },
      // Microsoft Surface Laptops
      { id: 'surface-laptop-7', name: 'Surface Laptop 7', brand: 'Microsoft', year: 2024 },
      { id: 'surface-laptop-6', name: 'Surface Laptop 6', brand: 'Microsoft', year: 2024 },
      { id: 'surface-laptop-studio-2', name: 'Surface Laptop Studio 2', brand: 'Microsoft', year: 2023 },
      { id: 'surface-laptop-5', name: 'Surface Laptop 5', brand: 'Microsoft', year: 2022 },
      { id: 'surface-laptop-go-3', name: 'Surface Laptop Go 3', brand: 'Microsoft', year: 2023 },
      { id: 'other-laptop', name: 'Other Laptop', brand: 'Other', year: null },
    ],
    services: [
      { id: 'screen', name: 'Screen Replacement', price: 'Call for Quote', time: '1-3 hours' },
      { id: 'battery', name: 'Battery Replacement', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'keyboard', name: 'Keyboard Replacement', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'trackpad', name: 'Trackpad Repair', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'charging-port', name: 'Charging Port Repair', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'upgrade', name: 'RAM/SSD Upgrade', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'other', name: 'Other Issue', price: 'Call for Quote', time: 'Varies' },
    ]
  },
  // COMPUTERS / DESKTOPS
  computers: {
    title: 'Computer Repair',
    description: 'Desktop, iMac, Mac Mini, PC repair',
    devices: [
      // iMac
      { id: 'imac-24-m4', name: 'iMac 24" M4', brand: 'Apple', year: 2024 },
      { id: 'imac-24-m3', name: 'iMac 24" M3', brand: 'Apple', year: 2023 },
      { id: 'imac-24-m1', name: 'iMac 24" M1', brand: 'Apple', year: 2021 },
      { id: 'imac-27-2020', name: 'iMac 27" (2020)', brand: 'Apple', year: 2020 },
      // Mac Mini
      { id: 'mac-mini-m4', name: 'Mac Mini M4', brand: 'Apple', year: 2024 },
      { id: 'mac-mini-m4-pro', name: 'Mac Mini M4 Pro', brand: 'Apple', year: 2024 },
      { id: 'mac-mini-m2', name: 'Mac Mini M2', brand: 'Apple', year: 2023 },
      { id: 'mac-mini-m2-pro', name: 'Mac Mini M2 Pro', brand: 'Apple', year: 2023 },
      { id: 'mac-mini-m1', name: 'Mac Mini M1', brand: 'Apple', year: 2020 },
      // Mac Studio
      { id: 'mac-studio-m2-ultra', name: 'Mac Studio M2 Ultra', brand: 'Apple', year: 2023 },
      { id: 'mac-studio-m2-max', name: 'Mac Studio M2 Max', brand: 'Apple', year: 2023 },
      { id: 'mac-studio-m1-ultra', name: 'Mac Studio M1 Ultra', brand: 'Apple', year: 2022 },
      // Mac Pro
      { id: 'mac-pro-m2-ultra', name: 'Mac Pro M2 Ultra', brand: 'Apple', year: 2023 },
      // Dell Desktop
      { id: 'dell-xps-desktop-2024', name: 'Dell XPS Desktop (2024)', brand: 'Dell', year: 2024 },
      { id: 'dell-inspiron-desktop-2024', name: 'Dell Inspiron Desktop (2024)', brand: 'Dell', year: 2024 },
      { id: 'dell-optiplex-7000', name: 'Dell OptiPlex 7000', brand: 'Dell', year: 2022 },
      // HP Desktop
      { id: 'hp-envy-desktop-2024', name: 'HP ENVY Desktop (2024)', brand: 'HP', year: 2024 },
      { id: 'hp-pavilion-desktop-2024', name: 'HP Pavilion Desktop (2024)', brand: 'HP', year: 2024 },
      // Custom PC
      { id: 'custom-gaming-pc', name: 'Custom Gaming PC', brand: 'Custom Build', year: null },
      { id: 'other-desktop', name: 'Other Desktop', brand: 'Other', year: null },
    ],
    services: [
      { id: 'screen', name: 'Screen/Display Repair', price: 'Call for Quote', time: '1-3 hours' },
      { id: 'upgrade', name: 'RAM/Storage Upgrade', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'power-supply', name: 'Power Supply Repair', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'motherboard', name: 'Motherboard Repair', price: 'Call for Quote', time: '2-5 days' },
      { id: 'other', name: 'Other Issue', price: 'Call for Quote', time: 'Varies' },
    ]
  },
  // PLAYSTATION
  playstation: {
    title: 'PlayStation Repair',
    description: 'PS5, PS4, PSVR, PlayStation Portal repair',
    devices: [
      // PlayStation 5 (2020-2024)
      { id: 'ps5-pro', name: 'PlayStation 5 Pro', brand: 'Sony', year: 2024 },
      { id: 'ps5-slim-digital', name: 'PlayStation 5 Slim Digital', brand: 'Sony', year: 2023 },
      { id: 'ps5-slim', name: 'PlayStation 5 Slim', brand: 'Sony', year: 2023 },
      { id: 'ps5-digital', name: 'PlayStation 5 Digital Edition', brand: 'Sony', year: 2020 },
      { id: 'ps5', name: 'PlayStation 5', brand: 'Sony', year: 2020 },
      // PlayStation 4
      { id: 'ps4-pro', name: 'PlayStation 4 Pro', brand: 'Sony', year: 2016 },
      { id: 'ps4-slim', name: 'PlayStation 4 Slim', brand: 'Sony', year: 2016 },
      { id: 'ps4', name: 'PlayStation 4', brand: 'Sony', year: 2013 },
      // PlayStation VR & Accessories
      { id: 'psvr2', name: 'PlayStation VR2', brand: 'Sony', year: 2023 },
      { id: 'ps-portal', name: 'PlayStation Portal', brand: 'Sony', year: 2023 },
      // Controllers
      { id: 'dualsense-edge', name: 'DualSense Edge Controller', brand: 'Sony', year: 2023 },
      { id: 'dualsense', name: 'DualSense Controller', brand: 'Sony', year: 2020 },
      { id: 'dualshock-4', name: 'DualShock 4 Controller', brand: 'Sony', year: 2013 },
      { id: 'other-playstation', name: 'Other PlayStation', brand: 'Sony', year: null },
    ],
    services: [
      { id: 'hdmi-port', name: 'HDMI Port Repair', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'disc-drive', name: 'Disc Drive Repair', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'overheating', name: 'Overheating Fix', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'power-supply', name: 'Power Supply Repair', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'controller', name: 'Controller Repair', price: 'Call for Quote', time: '30-60 min' },
      { id: 'cleaning', name: 'Deep Cleaning', price: 'Call for Quote', time: '1 hour' },
      { id: 'other', name: 'Other Issue', price: 'Call for Quote', time: 'Varies' },
    ]
  },
  // XBOX
  xbox: {
    title: 'Xbox Repair',
    description: 'Xbox Series X|S, Xbox One repair',
    devices: [
      // Xbox Series (2020-2024)
      { id: 'xbox-series-x', name: 'Xbox Series X', brand: 'Microsoft', year: 2020 },
      { id: 'xbox-series-s-1tb', name: 'Xbox Series S (1TB)', brand: 'Microsoft', year: 2024 },
      { id: 'xbox-series-s-carbon', name: 'Xbox Series S Carbon Black', brand: 'Microsoft', year: 2023 },
      { id: 'xbox-series-s', name: 'Xbox Series S', brand: 'Microsoft', year: 2020 },
      // Xbox One
      { id: 'xbox-one-x', name: 'Xbox One X', brand: 'Microsoft', year: 2017 },
      { id: 'xbox-one-s-digital', name: 'Xbox One S All-Digital', brand: 'Microsoft', year: 2019 },
      { id: 'xbox-one-s', name: 'Xbox One S', brand: 'Microsoft', year: 2016 },
      { id: 'xbox-one', name: 'Xbox One', brand: 'Microsoft', year: 2013 },
      // Controllers
      { id: 'xbox-elite-controller-2', name: 'Xbox Elite Controller Series 2', brand: 'Microsoft', year: 2019 },
      { id: 'xbox-wireless-controller', name: 'Xbox Wireless Controller', brand: 'Microsoft', year: 2020 },
      { id: 'other-xbox', name: 'Other Xbox', brand: 'Microsoft', year: null },
    ],
    services: [
      { id: 'hdmi-port', name: 'HDMI Port Repair', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'disc-drive', name: 'Disc Drive Repair', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'overheating', name: 'Overheating Fix', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'power-supply', name: 'Power Supply Repair', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'controller', name: 'Controller Repair', price: 'Call for Quote', time: '30-60 min' },
      { id: 'cleaning', name: 'Deep Cleaning', price: 'Call for Quote', time: '1 hour' },
      { id: 'other', name: 'Other Issue', price: 'Call for Quote', time: 'Varies' },
    ]
  },
  // NINTENDO
  nintendo: {
    title: 'Nintendo Repair',
    description: 'Nintendo Switch OLED, Switch, Switch Lite repair',
    devices: [
      // Nintendo Switch (2017-2024)
      { id: 'switch-2', name: 'Nintendo Switch 2', brand: 'Nintendo', year: 2025 },
      { id: 'switch-oled', name: 'Nintendo Switch OLED', brand: 'Nintendo', year: 2021 },
      { id: 'switch-v2', name: 'Nintendo Switch V2', brand: 'Nintendo', year: 2019 },
      { id: 'switch-lite', name: 'Nintendo Switch Lite', brand: 'Nintendo', year: 2019 },
      { id: 'switch-original', name: 'Nintendo Switch (Original)', brand: 'Nintendo', year: 2017 },
      // Nintendo 3DS/2DS
      { id: 'new-3ds-xl', name: 'New Nintendo 3DS XL', brand: 'Nintendo', year: 2015 },
      { id: 'new-2ds-xl', name: 'New Nintendo 2DS XL', brand: 'Nintendo', year: 2017 },
      { id: '3ds-xl', name: 'Nintendo 3DS XL', brand: 'Nintendo', year: 2012 },
      { id: '2ds', name: 'Nintendo 2DS', brand: 'Nintendo', year: 2013 },
      // Controllers
      { id: 'joycon-pair', name: 'Joy-Con (Pair)', brand: 'Nintendo', year: 2017 },
      { id: 'switch-pro-controller', name: 'Switch Pro Controller', brand: 'Nintendo', year: 2017 },
      { id: 'other-nintendo', name: 'Other Nintendo', brand: 'Nintendo', year: null },
    ],
    services: [
      { id: 'screen', name: 'Screen Replacement', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'joycon', name: 'Joy-Con Drift Repair', price: 'Call for Quote', time: '30-60 min' },
      { id: 'joycon-rail', name: 'Joy-Con Rail Repair', price: 'Call for Quote', time: '30-60 min' },
      { id: 'battery', name: 'Battery Replacement', price: 'Call for Quote', time: '1 hour' },
      { id: 'charging-port', name: 'Charging Port Repair', price: 'Call for Quote', time: '1 hour' },
      { id: 'game-slot', name: 'Game Card Slot Repair', price: 'Call for Quote', time: '1 hour' },
      { id: 'other', name: 'Other Issue', price: 'Call for Quote', time: 'Varies' },
    ]
  },
  // SMARTWATCHES
  watches: {
    title: 'Smartwatch Repair',
    description: 'Apple Watch, Samsung Galaxy Watch, Garmin, Fitbit repair',
    devices: [
      // Apple Watch (2020-2025)
      { id: 'apple-watch-ultra-3', name: 'Apple Watch Ultra 3', brand: 'Apple', year: 2025 },
      { id: 'apple-watch-series-11', name: 'Apple Watch Series 11', brand: 'Apple', year: 2025 },
      { id: 'apple-watch-se-3rd-gen', name: 'Apple Watch SE (3rd Gen)', brand: 'Apple', year: 2025 },
      { id: 'apple-watch-ultra-2', name: 'Apple Watch Ultra 2', brand: 'Apple', year: 2024 },
      { id: 'apple-watch-series-10', name: 'Apple Watch Series 10', brand: 'Apple', year: 2024 },
      { id: 'apple-watch-ultra', name: 'Apple Watch Ultra', brand: 'Apple', year: 2022 },
      { id: 'apple-watch-series-9', name: 'Apple Watch Series 9', brand: 'Apple', year: 2023 },
      { id: 'apple-watch-se-2nd-gen', name: 'Apple Watch SE (2nd Gen)', brand: 'Apple', year: 2022 },
      { id: 'apple-watch-series-8', name: 'Apple Watch Series 8', brand: 'Apple', year: 2022 },
      { id: 'apple-watch-series-7', name: 'Apple Watch Series 7', brand: 'Apple', year: 2021 },
      { id: 'apple-watch-se', name: 'Apple Watch SE', brand: 'Apple', year: 2020 },
      { id: 'apple-watch-series-6', name: 'Apple Watch Series 6', brand: 'Apple', year: 2020 },
      // Samsung Galaxy Watch (2020-2025)
      { id: 'galaxy-watch-ultra', name: 'Galaxy Watch Ultra', brand: 'Samsung', year: 2024 },
      { id: 'galaxy-watch-7', name: 'Galaxy Watch 7', brand: 'Samsung', year: 2024 },
      { id: 'galaxy-watch-fe', name: 'Galaxy Watch FE', brand: 'Samsung', year: 2024 },
      { id: 'galaxy-watch-6-classic', name: 'Galaxy Watch 6 Classic', brand: 'Samsung', year: 2023 },
      { id: 'galaxy-watch-6', name: 'Galaxy Watch 6', brand: 'Samsung', year: 2023 },
      { id: 'galaxy-watch-5-pro', name: 'Galaxy Watch 5 Pro', brand: 'Samsung', year: 2022 },
      { id: 'galaxy-watch-5', name: 'Galaxy Watch 5', brand: 'Samsung', year: 2022 },
      { id: 'galaxy-watch-4-classic', name: 'Galaxy Watch 4 Classic', brand: 'Samsung', year: 2021 },
      { id: 'galaxy-watch-4', name: 'Galaxy Watch 4', brand: 'Samsung', year: 2021 },
      // Garmin
      { id: 'garmin-fenix-8', name: 'Garmin Fenix 8', brand: 'Garmin', year: 2024 },
      { id: 'garmin-enduro-3', name: 'Garmin Enduro 3', brand: 'Garmin', year: 2024 },
      { id: 'garmin-forerunner-965', name: 'Garmin Forerunner 965', brand: 'Garmin', year: 2023 },
      { id: 'garmin-venu-3', name: 'Garmin Venu 3', brand: 'Garmin', year: 2023 },
      { id: 'garmin-epix-pro', name: 'Garmin Epix Pro', brand: 'Garmin', year: 2023 },
      // Fitbit (Google)
      { id: 'google-pixel-watch-3', name: 'Google Pixel Watch 3', brand: 'Google', year: 2024 },
      { id: 'google-pixel-watch-2', name: 'Google Pixel Watch 2', brand: 'Google', year: 2023 },
      { id: 'fitbit-sense-2', name: 'Fitbit Sense 2', brand: 'Fitbit', year: 2022 },
      { id: 'fitbit-versa-4', name: 'Fitbit Versa 4', brand: 'Fitbit', year: 2022 },
      { id: 'fitbit-charge-6', name: 'Fitbit Charge 6', brand: 'Fitbit', year: 2023 },
      { id: 'other-watch', name: 'Other Smartwatch', brand: 'Other', year: null },
    ],
    services: [
      { id: 'screen', name: 'Screen Replacement', price: 'Call for Quote', time: '1-2 hours' },
      { id: 'battery', name: 'Battery Replacement', price: 'Call for Quote', time: '1 hour' },
      { id: 'crown', name: 'Crown/Button Repair', price: 'Call for Quote', time: '30-60 min' },
      { id: 'speaker', name: 'Speaker Repair', price: 'Call for Quote', time: '1 hour' },
      { id: 'water-damage', name: 'Water Damage Repair', price: 'Call for Quote', time: '1-3 days' },
      { id: 'other', name: 'Other Issue', price: 'Call for Quote', time: 'Varies' },
    ]
  }
};

const RepairFlowPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [step, setStep] = useState(1);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const [contactInfo, setContactInfo] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brandFilter, setBrandFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [estimatedTotal, setEstimatedTotal] = useState(0);
  const [dbPrices, setDbPrices] = useState({}); // Dynamic prices from database
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false); // No loading needed - using static data
  const [apiDevices, setApiDevices] = useState([]); // Devices fetched from admin product manager
  
  // Email auto-detect for returning customers
  const [emailChecking, setEmailChecking] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerToken, setCustomerToken] = useState(null);

  // Check if email exists when user finishes typing
  const checkEmailExists = useCallback(async (email) => {
    if (!email || email.length < 5 || !email.includes('@')) return;
    
    setEmailChecking(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/customer/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (response.ok) {
        const data = await response.json();
        setAccountExists(data.exists);
        if (data.exists && !isLoggedIn) {
          setShowLoginForm(true);
        }
      }
    } catch (error) {
      console.error('Email check failed:', error);
    }
    setEmailChecking(false);
  }, [isLoggedIn]);

  // Handle customer login
  const handleCustomerLogin = async () => {
    if (!contactInfo.email || !loginPassword) return;
    
    setIsLoggingIn(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/customer/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: contactInfo.email, password: loginPassword })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomerToken(data.access_token);
        localStorage.setItem('customer_token', data.access_token);
        if (data.customer) localStorage.setItem('customer_data', JSON.stringify(data.customer));
        setIsLoggedIn(true);
        setShowLoginForm(false);
        setAccountExists(false);
        
        // Fetch customer profile to pre-fill form
        const profileResponse = await fetch(`${API_URL}/api/auth/customer/profile`, {
          headers: { 'Authorization': `Bearer ${data.access_token}` }
        });
        
        if (profileResponse.ok) {
          const profile = await profileResponse.json();
          localStorage.setItem('customer_data', JSON.stringify({ id: profile.id || '', name: profile.name || '', email: profile.email || '', phone: profile.phone || '' }));
          setContactInfo({
            name: profile.name || '',
            email: profile.email || contactInfo.email,
            phone: profile.phone || '',
            address: profile.address || '',
            city: profile.city || '',
            state: profile.state || '',
            zip: profile.zip || ''
          });
          toast({ title: "Welcome back!", description: "Your information has been pre-filled." });
        }
      } else {
        toast({ title: "Login failed", description: "Invalid email or password", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Login failed. Please try again.", variant: "destructive" });
    }
    setIsLoggingIn(false);
  };

  // Fetch repair catalog from API and merge with static catalog
  // This ensures admin-added devices show up on the frontend
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const response = await fetch(`${API_URL}/api/products/repair-catalog`);
        if (response.ok) {
          const data = await response.json();
          // Extract devices from API catalog for the current category
          const catSlug = category === 'macbook' ? 'laptops' : category === 'ipad' ? 'tablets' : category;
          const apiCat = (data.catalog || []).find(c => c.slug === catSlug);
          if (apiCat) {
            const devices = [];
            for (const brand of (apiCat.brands || [])) {
              for (const item of (brand.items || [])) {
                devices.push({
                  id: (item.slug || item.id || '').replace(/-sell$/, ''),
                  name: item.name,
                  brand: brand.name,
                  year: item.year || null
                });
              }
            }
            setApiDevices(devices);
          }
        }
      } catch (error) {
        console.error('Failed to fetch repair catalog:', error);
      }
      setIsLoadingCatalog(false);
    };
    fetchCatalog();
  }, [category]);

  // Get category data from static catalog merged with API devices
  // Handle URL aliases: /repairs/macbook → laptops, /repairs/ipad → tablets
  const categoryKey = category === 'macbook' ? 'laptops' : category === 'ipad' ? 'tablets' : category;
  const baseCategoryData = staticRepairCatalog[categoryKey] 
    ? staticRepairCatalog[categoryKey] 
    : { title: 'Repair', description: '', devices: [], services: defaultServices };
  
  // Merge API devices that don't exist in static catalog
  const categoryData = useMemo(() => {
    if (!apiDevices.length) return baseCategoryData;
    
    const staticIds = new Set(baseCategoryData.devices.map(d => d.id));
    const staticNames = new Set(baseCategoryData.devices.map(d => d.name.toLowerCase()));
    
    const newDevices = apiDevices.filter(d => {
      return !staticIds.has(d.id) && !staticNames.has(d.name.toLowerCase());
    });
    
    if (!newDevices.length) return baseCategoryData;
    
    return {
      ...baseCategoryData,
      devices: [...baseCategoryData.devices, ...newDevices]
    };
  }, [baseCategoryData, apiDevices]);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch prices from database (website_repair_prices collection)
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const timestamp = Date.now();
        const url = `${API_URL}/api/prices/repairs?_t=${timestamp}`;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          const priceMap = {};
          
          // Build a reverse mapping: DB model name slug → frontend device ID
          // e.g., "playstation-5-pro" → "ps5-pro"
          const dbKeyToFrontendId = {};
          Object.values(staticRepairCatalog).forEach(cat => {
            cat.devices.forEach(d => {
              const dbKey = d.name.toLowerCase().replace(/\s+/g, '-').replace(/[()'"]/g, '');
              if (dbKey !== d.id) {
                dbKeyToFrontendId[dbKey] = d.id;
              }
            });
          });
          // Also map API devices (admin-added)
          apiDevices.forEach(d => {
            const dbKey = d.name.toLowerCase().replace(/\s+/g, '-').replace(/[()'"]/g, '');
            if (dbKey !== d.id) {
              dbKeyToFrontendId[dbKey] = d.id;
            }
          });
          
          if (data.prices && data.prices.length > 0) {
            data.prices.forEach(price => {
              const modelKey = price.device_model?.toLowerCase().replace(/\s+/g, '-').replace(/[()'"]/g, '');
              if (!priceMap[modelKey]) priceMap[modelKey] = {};
              
              // Also store under frontend device ID if different
              const frontendId = dbKeyToFrontendId[modelKey];
              if (frontendId && !priceMap[frontendId]) priceMap[frontendId] = {};
              
              const repairTypeMap = {
                'Screen Replacement': 'screen',
                'Screen/Display Repair': 'screen',
                'Battery Replacement': 'battery',
                'Charging Port Repair': 'charging-port',
                'Charging Port': 'charging-port',
                'Back Glass Replacement': 'back-glass',
                'Back Glass': 'back-glass',
                'Camera Repair': 'camera',
                'Speaker Repair': 'speaker',
                'Speaker/Mic Repair': 'speaker',
                'Speaker/Mic': 'speaker',
                'Water Damage Repair': 'water-damage',
                'Water Damage': 'water-damage',
                'Face ID Repair': 'face-id',
                'Face ID': 'face-id',
                'Button Repair': 'button',
                'Crown/Button Repair': 'crown',
                'Keyboard Replacement': 'keyboard',
                'Trackpad Repair': 'trackpad',
                'RAM/SSD Upgrade': 'upgrade',
                'RAM/Storage Upgrade': 'upgrade',
                'Power Supply Repair': 'power-supply',
                'Motherboard Repair': 'motherboard',
                'HDMI Port Repair': 'hdmi-port',
                'Disc Drive Repair': 'disc-drive',
                'Overheating Fix': 'overheating',
                'Controller Repair': 'controller',
                'Deep Cleaning': 'cleaning',
                'Deep Cleaning Service': 'cleaning',
                'Joy-Con Drift Repair': 'joycon',
                'Joy-Con Rail Repair': 'joycon-rail',
                'Game Card Slot Repair': 'game-slot',
                'Other Repair': 'other',
                'Other Issue': 'other',
                'Other Issue (Describe below)': 'other',
              };
              
              const serviceId = repairTypeMap[price.repair_type] || price.repair_type?.toLowerCase().replace(/\s+/g, '-').replace(/[()'"]/g, '');
              if (price.price !== undefined && price.price !== null) {
                // Prefer non-zero prices: don't let $0 overwrite an existing non-zero price
                if (!priceMap[modelKey][serviceId] || price.price > 0) {
                  priceMap[modelKey][serviceId] = price.price;
                }
                // Also store under frontend ID
                if (frontendId) {
                  if (!priceMap[frontendId][serviceId] || price.price > 0) {
                    priceMap[frontendId][serviceId] = price.price;
                  }
                }
              }
            });
            setDbPrices(priceMap);
          }
        }
      } catch (error) {
        console.error('Failed to fetch prices:', error);
      }
    };
    fetchPrices();
  }, [category]);

  // Default fallback prices - Screen Replacement prices for iPhone 12-17
  const fallbackPrices = {
    // iPhone 17 Series (2025)
    'iphone-17-pro-max': { screen: 235, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-17-pro': { screen: 185, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-17-air': { screen: 175, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-17': { screen: 175, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    // iPhone 16 Series
    'iphone-16-pro-max': { screen: 235, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-16-pro': { screen: 185, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-16e': { screen: 90, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-16-plus': { screen: 175, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-16': { screen: 175, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    // iPhone 15 Series
    'iphone-15-pro-max': { screen: 185, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-15-pro': { screen: 175, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-15-plus': { screen: 185, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-15': { screen: 155, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    // iPhone 14 Series
    'iphone-14-pro-max': { screen: 185, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-14-pro': { screen: 175, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-14-plus': { screen: 90, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-14': { screen: 90, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    // iPhone 13 Series
    'iphone-13-pro-max': { screen: 85, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-13-pro': { screen: 70, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-13': { screen: 75, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-13-mini': { screen: 75, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    // iPhone 12 Series
    'iphone-12-pro-max': { screen: 90, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-12-pro': { screen: 85, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-12': { screen: 75, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-12-mini': { screen: 70, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    // Older iPhones
    'iphone-11-pro-max': { screen: 75, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-11-pro': { screen: 70, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-11': { screen: 65, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-x': { screen: 65, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-xs': { screen: 65, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-xs-max': { screen: 65, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'iphone-xr': { screen: 65, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    // Samsung
    'samsung-s25-ultra': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-s25-plus': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-s25': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-s24-ultra': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-s24-plus': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-s24': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-s23-ultra': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-fold-6': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-fold-5': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-flip-6': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'samsung-flip-5': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    // Google Pixel
    'pixel-10-pro-xl': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'pixel-10-pro': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'pixel-9-pro-xl': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'pixel-9-pro': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    'pixel-8-pro': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0 },
    // Default prices for unlisted devices
    'default': { screen: 0, battery: 0, 'charging-port': 0, 'back-glass': 0, camera: 0, speaker: 0, 'water-damage': 0, button: 0, 'face-id': 0, software: 0, other: 0 },
  };

  // Calculate exact price for a service (DB prices first, then fallback)
  const getExactPrice = (deviceId, serviceId) => {
    // Check database prices first (use explicit null check, not truthiness - $0 is valid)
    if (dbPrices[deviceId] && dbPrices[deviceId][serviceId] != null) {
      return dbPrices[deviceId][serviceId];
    }
    // Fall back to hardcoded prices (default to 0, not 79)
    const devicePrices = fallbackPrices[deviceId] ?? fallbackPrices['default'];
    return devicePrices[serviceId] ?? fallbackPrices['default'][serviceId] ?? 0;
  };

  // Calculate total when services change
  useEffect(() => {
    if (selectedDevice && selectedServices.length > 0) {
      const total = selectedServices.reduce((sum, serviceId) => {
        return sum + getExactPrice(selectedDevice.id, serviceId);
      }, 0);
      setEstimatedTotal(total);
    } else {
      setEstimatedTotal(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice, selectedServices, dbPrices]);
  
  // Get unique brands for filter
  const brands = ['all', ...new Set(categoryData.devices.map(d => d.brand))];
  
  // Filter devices by brand and search
  const filteredDevices = categoryData.devices.filter(d => {
    const matchesBrand = brandFilter === 'all' || d.brand === brandFilter;
    const matchesSearch = d.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesBrand && matchesSearch;
  });

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const calculateTotal = () => {
    const selected = selectedServices.map(id => categoryData.services.find(s => s.id === id));
    if (selected.length === 0) return null;
    return `${selected.length} service(s) selected`;
  };

  const handleSubmit = async () => {
    if (!contactInfo.name || !contactInfo.email || !contactInfo.phone) {
      toast({ title: "Error", description: "Please fill in all contact fields", variant: "destructive" });
      return;
    }
    
    if (deliveryMethod === 'mail-in' && (!contactInfo.address || !contactInfo.city || !contactInfo.state || !contactInfo.zip)) {
      toast({ title: "Error", description: "Please fill in your shipping address for mail-in service", variant: "destructive" });
      return;
    }

    if (!agreedToTerms) {
      toast({ title: "Error", description: "Please agree to the terms and conditions", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      // Generate formatted service issues with individual prices
      const formattedIssues = selectedServices.map(id => {
        const service = categoryData.services.find(s => s.id === id);
        const exactPrice = selectedDevice ? getExactPrice(selectedDevice.id, id) : 0;
        return `${service?.name} ($${exactPrice})`;
      });
      
      const requestData = {
        category: categoryData.title,
        device_type: selectedDevice?.brand || '',
        device_model: selectedDevice?.name || '',
        issues: formattedIssues,
        description: additionalDetails,
        delivery_method: deliveryMethod,
        customer_name: contactInfo.name,
        customer_email: contactInfo.email,
        customer_phone: contactInfo.phone,
        // Include estimated price for email and packing slip
        estimated_price: estimatedTotal > 0 ? estimatedTotal.toString() : null,
        // Account creation
        create_account: createAccount,
        password: createAccount ? password : null
      };
      
      if (deliveryMethod === 'mail-in') {
        requestData.customer_address = contactInfo.address;
        requestData.customer_city = contactInfo.city;
        requestData.customer_state = contactInfo.state;
        requestData.customer_zip = contactInfo.zip;
      }
      
      const response = await fetch(`${API_URL}/api/repair-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        // Track conversion for Google Ads
        try {
          const { trackRepairFlowConversion } = await import('../services/analytics');
          trackRepairFlowConversion(
            selectedDevice?.name || categoryData.title,
            selectedServices.map(id => categoryData.services.find(s => s.id === id)?.name).join(', '),
            estimatedTotal
          );
        } catch(e) { /* analytics optional */ }
        setStep(6);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-32 pb-20 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      <div className="absolute top-20 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Loading State */}
      {isLoadingCatalog && (
        <div className="fixed inset-0 bg-slate-950/80 flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-red-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-300">Loading repair options...</p>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Back Button */}
        <button 
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/repairs')}
          className="flex items-center text-slate-400 hover:text-red-500 mb-6 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" /> 
          {step > 1 ? 'Back' : 'All Categories'}
        </button>

        {/* Progress Bar */}
        {step < 6 && (
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm mb-3">
              <span className="text-slate-400">Step {step} of 5</span>
              <span className="text-red-500 font-medium">{Math.round((step / 5) * 100)}%</span>
            </div>
            <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-red-600 to-red-500 transition-all duration-500" 
                style={{ width: `${(step / 5) * 100}%` }} 
              />
            </div>
            <div className="flex justify-between mt-2">
              {['Device', 'Services', 'Delivery', 'Contact', 'Review'].map((label, idx) => (
                <span 
                  key={label}
                  className={`text-xs ${idx + 1 <= step ? 'text-red-500' : 'text-slate-600'}`}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Select Device */}
        {step === 1 && (
          <div className="animate-in fade-in duration-300">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full mb-4">
                <Wrench className="w-4 h-4 text-red-500" />
                <span className="text-red-400 text-sm font-medium">{categoryData.title}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                {REPAIR_CATEGORY_SEO[category]?.h1 ? (
                  <>
                    {REPAIR_CATEGORY_SEO[category].h1[0]}{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">{REPAIR_CATEGORY_SEO[category].h1[1]}</span>
                  </>
                ) : (
                  <>Select Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Device</span></>
                )}
              </h1>
              <p className="text-slate-400">Choose your device model to get started</p>
            </div>
            
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="text"
                placeholder="Search devices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-900/50 border border-slate-800 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-red-500/50 transition-colors"
              />
            </div>

            {/* Brand Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              {brands.map(brand => (
                <button
                  key={brand}
                  onClick={() => setBrandFilter(brand)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    brandFilter === brand 
                      ? 'bg-red-600 text-white shadow-lg shadow-red-600/25' 
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  }`}
                >
                  {brand === 'all' ? 'All Brands' : brand}
                </button>
              ))}
            </div>
            
            {/* Devices Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[450px] overflow-y-auto pr-2 custom-scrollbar">
              {filteredDevices.map((device) => {
                const isOther = device.name?.toLowerCase().includes('other') || device.id?.toLowerCase().includes('other');
                return (
                <div
                  key={device.id}
                  onClick={() => { setSelectedDevice(device); setStep(2); }}
                  className="group bg-slate-900/50 border border-slate-800 rounded-xl p-4 cursor-pointer transition-all hover:border-red-500/50 hover:bg-slate-900/80 hover:shadow-lg hover:shadow-red-500/5"
                  data-testid={`device-${device.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white group-hover:text-red-400 transition-colors">{device.name}</p>
                      {device.year && <p className="text-xs text-slate-500 mt-0.5">({device.year})</p>}
                      {isOther && (
                        <p className="text-xs text-amber-400 mt-1">📞 Contact us for pricing & details</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-red-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              )})}
            </div>

            {filteredDevices.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500">No devices found</p>
                <button onClick={() => { setSearchTerm(''); setBrandFilter('all'); }} className="text-red-500 text-sm mt-2 hover:underline">
                  Clear filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Services */}
        {step === 2 && (
          <div className="animate-in fade-in duration-300">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg mb-4">
              <span className="text-slate-400 text-sm">Selected:</span>
              <span className="text-white font-medium text-sm">{selectedDevice?.name}</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              What Needs <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Repair?</span>
            </h1>
            <p className="text-slate-400 mb-8">Select all services you need</p>
            
            <div className="space-y-3 mb-6">
              {categoryData.services.map((service) => {
                const exactPrice = getExactPrice(selectedDevice?.id, service.id);
                return (
                <div
                  key={service.id}
                  onClick={() => toggleService(service.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedServices.includes(service.id)
                      ? 'border-red-500 bg-red-500/10'
                      : 'border-slate-800 bg-slate-900/50 hover:border-slate-700'
                  }`}
                  data-testid={`service-${service.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-white">{service.name}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-red-500 font-bold text-lg">${exactPrice}</span>
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {service.time}
                        </span>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                      selectedServices.includes(service.id) 
                        ? 'border-red-500 bg-red-500' 
                        : 'border-slate-600'
                    }`}>
                      {selectedServices.includes(service.id) && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                </div>
              );
              })}
            </div>

            {selectedServices.includes('other') && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">Describe the issue</label>
                <Textarea
                  value={additionalDetails}
                  onChange={(e) => setAdditionalDetails(e.target.value)}
                  placeholder="Please describe what's wrong with your device..."
                  className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50"
                  rows={3}
                />
              </div>
            )}

            {estimatedTotal > 0 && (
              <div className="bg-gradient-to-r from-red-900/30 to-slate-900 border border-red-500/30 rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Estimated Total</p>
                    <p className="text-3xl font-bold text-white">${estimatedTotal}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500 text-xs">{selectedServices.length} service(s) selected</p>
                    <p className="text-slate-400 text-xs">Final price after inspection</p>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={() => setStep(3)}
              disabled={selectedServices.length === 0}
              className="w-full bg-red-600 hover:bg-red-500 text-white py-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 3: Delivery Method */}
        {step === 3 && (
          <div className="animate-in fade-in duration-300">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              How Would You Like to <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Proceed?</span>
            </h1>
            <p className="text-slate-400 mb-8">Choose your preferred service option</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {/* Walk-In */}
              <div
                onClick={() => setDeliveryMethod('walk-in')}
                className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                  deliveryMethod === 'walk-in'
                    ? 'border-red-500 bg-red-500/10'
                    : 'border-slate-800 bg-slate-900/50 hover:border-slate-700'
                }`}
                data-testid="delivery-walk-in"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Walk-In Service</h3>
                <p className="text-slate-400 text-sm mb-4">Visit our store for same-day repairs</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Zap className="w-4 h-4 text-yellow-500" /> Same-day service
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Shield className="w-4 h-4 text-green-500" /> Free diagnostics
                  </li>
                </ul>
                {deliveryMethod === 'walk-in' && (
                  <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>

              {/* Mail-In */}
              <div
                onClick={() => setDeliveryMethod('mail-in')}
                className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                  deliveryMethod === 'mail-in'
                    ? 'border-red-500 bg-red-500/10'
                    : 'border-slate-800 bg-slate-900/50 hover:border-slate-700'
                }`}
                data-testid="delivery-mail-in"
              >
                <div className="absolute -top-3 -right-3 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                  FREE SHIPPING
                </div>
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                  <Package className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Mail-In Repair</h3>
                <p className="text-slate-400 text-sm mb-4">Ship your device to us for repair</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Package className="w-4 h-4 text-purple-500" /> Free prepaid label
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Shield className="w-4 h-4 text-green-500" /> Insured shipping
                  </li>
                </ul>
                {deliveryMethod === 'mail-in' && (
                  <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            </div>

            {/* Walk-In Appointment Option */}
            {deliveryMethod === 'walk-in' && (
              <div className="mt-6 p-5 bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-xl border border-green-500/30">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-white mb-1">Walk-ins Welcome Anytime!</h4>
                    <p className="text-slate-300 text-sm mb-3">
                      No appointment needed. Visit us during business hours: <span className="text-green-400 font-medium">Mon-Fri 10AM-6PM, Sat 10AM-4PM</span>
                    </p>
                    <p className="text-slate-400 text-sm mb-4">
                      📍 4859 W Irving Park Rd, Chicago, IL 60641
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <a href="/book-appointment" className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition-colors">
                        <Clock className="w-4 h-4" /> Book Appointment (Optional)
                      </a>
                      <a href="https://www.google.com/maps/dir/?api=1&destination=4859+W+Irving+Park+Rd,+Chicago,+IL+60641" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors">
                        <MapPin className="w-4 h-4" /> Get Directions
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={() => setStep(4)}
              disabled={!deliveryMethod}
              className="w-full bg-red-600 hover:bg-red-500 text-white py-6 rounded-xl disabled:opacity-50"
            >
              Continue
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 4: Contact Information */}
        {step === 4 && (
          <div className="animate-in fade-in duration-300">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Information</span>
            </h1>
            <p className="text-slate-400 mb-8">We&apos;ll use this to contact you about your repair</p>
            
            {/* Logged In Banner */}
            {isLoggedIn && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-green-400 font-medium">Welcome back, {contactInfo.name}!</p>
                  <p className="text-green-300/70 text-sm">Your info has been pre-filled from your account</p>
                </div>
              </div>
            )}
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Full Name *</label>
                <Input
                  value={contactInfo.name}
                  onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50 py-6"
                />
              </div>
              
              {/* Email with auto-detect */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                <Input
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => {
                    setContactInfo({ ...contactInfo, email: e.target.value });
                    setAccountExists(false);
                    setShowLoginForm(false);
                  }}
                  onBlur={(e) => !isLoggedIn && checkEmailExists(e.target.value)}
                  placeholder="john@example.com"
                  className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50 py-6"
                  disabled={isLoggedIn}
                />
                {emailChecking && (
                  <p className="text-slate-400 text-xs mt-1">Checking...</p>
                )}
              </div>
              
              {/* Returning Customer Login Prompt */}
              {showLoginForm && !isLoggedIn && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl animate-in fade-in duration-300">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <LogIn className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-blue-400 font-medium">Welcome back!</p>
                      <p className="text-blue-300/70 text-sm">Log in to pre-fill your information</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="relative">
                      <Input
                        type={showLoginPassword ? "text" : "password"}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="bg-slate-900/50 border-slate-700 text-white py-4 pr-10"
                        onKeyDown={(e) => e.key === 'Enter' && handleCustomerLogin()}
                      />
                      <button 
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                      >
                        {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleCustomerLogin}
                        disabled={isLoggingIn || !loginPassword}
                        className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
                      >
                        {isLoggingIn ? 'Logging in...' : 'Log In'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowLoginForm(false);
                          setAccountExists(false);
                        }}
                        className="border-slate-600 text-slate-300 hover:bg-slate-800"
                      >
                        Skip
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number *</label>
                <Input
                  value={contactInfo.phone}
                  onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50 py-6"
                />
              </div>

              {/* ── SMS Opt-In (TCPA compliance), placed RIGHT AFTER phone field
                  so it's clearly part of the contact form. Shown for BOTH
                  walk-in AND mail-in customers before Review Order. ── */}
              <div
                className="rounded-xl border-2 border-cyan-500/50 bg-cyan-50 dark:bg-cyan-500/10 p-4 shadow-sm"
                data-testid="repair-manual-sms-block"
              >
                <SmsOptInCheckbox
                  checked={smsOptIn}
                  onChange={setSmsOptIn}
                  accentColor="cyan"
                  testIdPrefix="repair-manual-sms-opt-in"
                />
              </div>

              {/* Create Account Option - only show if not logged in */}
              {!isLoggedIn && (
              <div className="pt-4 border-t border-slate-800">
                <div 
                  onClick={() => {
                    setCreateAccount(!createAccount);
                    if (createAccount) {
                      setPassword('');
                      setConfirmPassword('');
                    }
                  }}
                  className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-slate-700 hover:border-red-500/30 transition-colors"
                  data-testid="create-account-toggle"
                >
                  <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    createAccount ? 'bg-red-500 border-red-500' : 'border-slate-600'
                  }`}>
                    {createAccount && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-white">Create an Account</span>
                    <p className="text-sm text-slate-400 mt-1">
                      Track your repairs, view history, and get faster checkout next time
                    </p>
                  </div>
                </div>
              </div>
              )}
              
              {!isLoggedIn && createAccount && (
                <div className="space-y-4 animate-in fade-in duration-300">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Password * (min 8 characters)</label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create a secure password"
                      className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50 py-6"
                      data-testid="account-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Confirm Password *</label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50 py-6"
                      data-testid="account-confirm-password"
                    />
                    {password && confirmPassword && password !== confirmPassword && (
                      <p className="text-red-400 text-sm mt-1">Passwords do not match</p>
                    )}
                    {password && password.length > 0 && password.length < 8 && (
                      <p className="text-amber-400 text-sm mt-1">Password must be at least 8 characters</p>
                    )}
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400">
                      🔒 Your password is securely encrypted. We never store or share your personal information.
                    </p>
                  </div>
                </div>
              )}

              {deliveryMethod === 'mail-in' && (
                <>
                  <div className="pt-4 border-t border-slate-800">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Package className="w-5 h-5 text-purple-500" />
                      Shipping Address
                    </h3>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Street Address *</label>
                    <Input
                      value={contactInfo.address}
                      onChange={(e) => setContactInfo(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Enter your street address"
                      className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50 py-6"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">City *</label>
                      <Input
                        value={contactInfo.city}
                        onChange={(e) => setContactInfo({ ...contactInfo, city: e.target.value })}
                        placeholder="Chicago"
                        className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50 py-6"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">State *</label>
                      <Input
                        value={contactInfo.state}
                        onChange={(e) => setContactInfo({ ...contactInfo, state: e.target.value })}
                        placeholder="IL"
                        className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50 py-6"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">ZIP Code *</label>
                    <Input
                      value={contactInfo.zip}
                      onChange={(e) => setContactInfo({ ...contactInfo, zip: e.target.value })}
                      placeholder="60601"
                      className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-red-500/50 py-6"
                    />
                  </div>
                </>
              )}
            </div>

            <Button
              onClick={() => setStep(5)}
              disabled={!contactInfo.name || !contactInfo.email || !contactInfo.phone || 
                (deliveryMethod === 'mail-in' && (!contactInfo.address || !contactInfo.city || !contactInfo.state || !contactInfo.zip)) ||
                (createAccount && (password.length < 8 || password !== confirmPassword))}
              className="w-full bg-red-600 hover:bg-red-500 text-white py-6 rounded-xl disabled:opacity-50"
            >
              Review Order
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 5: Review */}
        {step === 5 && (
          <div className="animate-in fade-in duration-300">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Review Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Request</span>
            </h1>
            <p className="text-slate-400 mb-8">Please confirm the details below</p>
            
            <div className="space-y-4 mb-8">
              {/* Device */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-slate-500 mb-2">DEVICE</h3>
                <p className="text-white font-semibold">{selectedDevice?.name}</p>
                <p className="text-slate-400 text-sm">{selectedDevice?.brand}</p>
              </div>

              {/* Services */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-slate-500 mb-3">SERVICES</h3>
                <div className="space-y-2">
                  {selectedServices.map(serviceId => {
                    const service = categoryData.services.find(s => s.id === serviceId);
                    return (
                      <div key={serviceId} className="flex justify-between items-center">
                        <span className="text-white">{service?.name}</span>
                        <span className="text-red-500 font-medium">{service?.price}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Delivery */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-slate-500 mb-2">DELIVERY METHOD</h3>
                <div className="flex items-center gap-3">
                  {deliveryMethod === 'walk-in' ? (
                    <>
                      <MapPin className="w-5 h-5 text-blue-500" />
                      <span className="text-white">Walk-In Service</span>
                    </>
                  ) : (
                    <>
                      <Package className="w-5 h-5 text-purple-500" />
                      <span className="text-white">Mail-In Repair</span>
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">FREE SHIPPING</span>
                    </>
                  )}
                </div>
              </div>

              {/* Contact */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-slate-500 mb-2">CONTACT INFO</h3>
                <p className="text-white">{contactInfo.name}</p>
                <p className="text-slate-400 text-sm">{contactInfo.email}</p>
                <p className="text-slate-400 text-sm">{contactInfo.phone}</p>
                {deliveryMethod === 'mail-in' && (
                  <p className="text-slate-400 text-sm mt-2">
                    {contactInfo.address}, {contactInfo.city}, {contactInfo.state} {contactInfo.zip}
                  </p>
                )}
              </div>

              {/* Terms Agreement */}
              <div className="bg-slate-900/80 border border-slate-700 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div 
                    onClick={() => setAgreedToTerms(!agreedToTerms)}
                    className={`w-6 h-6 mt-0.5 rounded border-2 cursor-pointer flex items-center justify-center flex-shrink-0 transition-colors ${
                      agreedToTerms 
                        ? 'bg-red-500 border-red-500' 
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    {agreedToTerms && <Check className="w-4 h-4 text-white" />}
                  </div>
                  <div className="text-sm text-slate-400">
                    <p className="mb-2">
                      <span className="text-white font-medium">I agree to the Terms & Conditions *</span>
                    </p>
                    <p className="text-xs leading-relaxed">
                      By checking this box, I acknowledge and agree that: (1) The repair quote is an estimate and final pricing may vary based on actual diagnosis; (2) 2A Electronics Service is <strong className="text-white">NOT responsible for any data loss</strong> - I confirm all personal data has been backed up before submitting my device; (3) Repairs may require additional parts or services not included in the initial quote; (4) 2A Electronics may use quality aftermarket parts unless OEM parts are specifically requested and available; (5) Unclaimed devices after 90 days may be considered abandoned; (6) The 1-month warranty covers only the specific repair performed, not pre-existing issues or new damage; (7) I am the legal owner or authorized to submit this device for repair; (8) I have removed all passcodes, Find My Device, and activation locks, or provided necessary credentials; (9) Water damage repair success is not guaranteed; (10) <strong className="text-white">For mail-in repairs: 2A Electronics is NOT responsible for packages lost, damaged, or stolen during shipping</strong> - we recommend purchasing additional shipping insurance for high-value devices; (11) Walk-in customers assume responsibility for their device once they leave the store; (12) I have read and agree to the <a href="/privacy-policy" className="text-red-500 hover:underline">Privacy Policy</a>, <a href="/terms-and-conditions" className="text-red-500 hover:underline">Terms of Service</a>, and <a href="/user-agreement" className="text-red-500 hover:underline">User Agreement</a>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <ProtectionPlanNotice className="mb-4" testId="repair-flow-protection-plan-notice" />

            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !agreedToTerms}
              className="w-full bg-red-600 hover:bg-red-500 text-white py-6 rounded-xl disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Repair Request'}
            </Button>
          </div>
        )}

        {/* Step 6: Confirmation */}
        {step === 6 && (
          <div className="animate-in fade-in duration-300 text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Request Submitted!</h1>
            <p className="text-slate-400 mb-8 max-w-md mx-auto">
              Thank you for choosing 2A Electronics Service. We&apos;ll contact you within 24 hours to confirm your repair.
            </p>
            
            {deliveryMethod === 'mail-in' && (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-8 text-left max-w-md mx-auto">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-500" />
                  Shipping Label
                </h3>
                <p className="text-slate-400 text-sm">
                  A prepaid shipping label will be sent to your email shortly. Print it out and attach it to your package.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Back to Home
              </Button>
              <Button
                onClick={() => navigate('/repairs')}
                className="bg-red-600 hover:bg-red-500"
              >
                Submit Another Request
              </Button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(100, 116, 139, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(100, 116, 139, 0.8);
        }
      `}</style>
    </div>
  );
};

export default RepairFlowPage;
